import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { MessageService, ConfirmationService } from 'primeng/api';
import { AuthService } from 'src/app/auth/auth.service';
import { SpinnerService } from 'src/app/common/services/spinner.service';
import {
  AppApiKeyPermissionRef,
  AppApiKeyRow,
  AppApiKeysService,
} from '../../services/app-api-keys.service';
import { PermissionsService } from 'src/app/permissions/service/permissions.service';

/** API key row normalized for PrimeNG column filters (Date values + permission count). */
export interface AppApiKeyTableRow extends Omit<
  AppApiKeyRow,
  'expiresAt' | 'lastUsedAt' | 'createdAt'
> {
  _permCount: number;
  expiresAt: Date | null;
  lastUsedAt: Date | null;
  createdAt: Date | null;
}

/** Permission row in the read-only modal (extra field for Abilities text filter). */
export interface PermissionModalRow extends AppApiKeyPermissionRef {
  _abilitiesText: string;
}

@Component({
  selector: 'app-app-api-keys',
  templateUrl: './app-api-keys.component.html',
  styleUrls: ['./app-api-keys.component.sass'],
})
export class AppApiKeysComponent implements OnInit {
  keys: AppApiKeyTableRow[] = [];
  loading = true;

  /** PrimeNG text column filter match modes */
  readonly textMatchModeOptions = [
    { label: 'Contains', value: 'contains' },
    { label: 'Equals', value: 'equals' },
    { label: 'Starts With', value: 'startsWith' },
    { label: 'Ends With', value: 'endsWith' },
  ];

  /** Options for p-multiSelect: CMS Permission documents. */
  permissionSelectOptions: { label: string; value: string }[] = [];

  createVisible = false;
  editVisible = false;
  secretVisible = false;
  secretTitle = '';
  secretValue = '';

  permissionsModalVisible = false;
  permissionsModalHeader = '';
  permissionsModalList: PermissionModalRow[] = [];

  createForm: {
    name: string;
    isActive: boolean;
    expiresAt: Date | null;
    permissions_ref: string[];
  } = {
    name: '',
    isActive: true,
    expiresAt: null,
    permissions_ref: [],
  };

  editForm: {
    id: number;
    name: string;
    isActive: boolean;
    expiresAt: Date | null;
    permissions_ref: string[];
  } = {
    id: 0,
    name: '',
    isActive: true,
    expiresAt: null,
    permissions_ref: [],
  };

  constructor(
    public authService: AuthService,
    private appApiKeysService: AppApiKeysService,
    private permissionsService: PermissionsService,
    private spinnerService: SpinnerService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {}

  ngOnInit(): void {
    void this.loadPermissionOptions();
    if (!this.authService.loadedPermissions()) {
      this.authService.loadPermissions().subscribe(() => this.loadKeys());
    } else {
      this.loadKeys();
    }
  }

  async loadPermissionOptions(): Promise<void> {
    try {
      const perms = await this.permissionsService.getAllPermissions();
      this.permissionSelectOptions = (perms || []).map((p) => ({
        label: `${p.name} (${p.entity})`,
        value: String(p._id),
      }));
    } catch {
      this.permissionSelectOptions = [];
    }
  }

  permissionCount(row: AppApiKeyTableRow): number {
    return row._permCount;
  }

  openPermissionsModal(row: AppApiKeyTableRow): void {
    this.permissionsModalHeader = `Permissions — ${row.name} (id ${row.id})`;
    this.permissionsModalList = (row.permissions_ref || []).map((p) => ({
      ...p,
      _abilitiesText: p.abilities?.length ? p.abilities.join(', ') : '',
    }));
    this.permissionsModalVisible = true;
  }

  abilitiesDisplay(abilities: string[] | undefined): string {
    return abilities?.length ? abilities.join(', ') : '—';
  }

  private permissionIdsFromRow(row: AppApiKeyTableRow): string[] {
    const refs = row.permissions_ref;
    if (!refs?.length) {
      return [];
    }
    return refs.map((p) => String(p._id));
  }

  async loadKeys(): Promise<void> {
    this.loading = true;
    try {
      const raw = await this.appApiKeysService.list();
      this.keys = this.normalizeRows(raw);
    } catch (e) {
      this.handleHttpError(e, 'Load API keys');
    } finally {
      this.loading = false;
    }
  }

  private normalizeRows(raw: AppApiKeyRow[]): AppApiKeyTableRow[] {
    return raw.map((k) => ({
      ...k,
      _permCount: k.permissions_ref?.length ?? 0,
      expiresAt: k.expiresAt ? new Date(k.expiresAt) : null,
      lastUsedAt: k.lastUsedAt ? new Date(k.lastUsedAt) : null,
      createdAt: k.createdAt ? new Date(k.createdAt) : null,
    }));
  }

  openCreate(): void {
    this.createForm = {
      name: '',
      isActive: true,
      expiresAt: null,
      permissions_ref: [],
    };
    this.createVisible = true;
  }

  async submitCreate(): Promise<void> {
    if (!this.createForm.name?.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation',
        detail: 'Name is required.',
      });
      return;
    }
    this.spinnerService.loadSpinner();
    try {
      const payload = {
        name: this.createForm.name.trim(),
        isActive: this.createForm.isActive,
        expiresAt: this.createForm.expiresAt
          ? this.createForm.expiresAt.toISOString()
          : undefined,
        permissions_ref:
          this.createForm.permissions_ref?.length > 0
            ? this.createForm.permissions_ref
            : undefined,
      };
      const created = await this.appApiKeysService.create(payload);
      this.createVisible = false;
      await this.loadKeys();
      this.showSecretDialog('API key created', created.apiKey);
    } catch (e) {
      this.handleHttpError(e, 'Create API key');
    } finally {
      this.spinnerService.endSpinner();
    }
  }

  openEdit(row: AppApiKeyTableRow): void {
    this.editForm = {
      id: row.id,
      name: row.name,
      isActive: row.isActive,
      expiresAt: row.expiresAt ? new Date(row.expiresAt) : null,
      permissions_ref: this.permissionIdsFromRow(row),
    };
    this.editVisible = true;
  }

  async submitEdit(): Promise<void> {
    if (!this.editForm.name?.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation',
        detail: 'Name is required.',
      });
      return;
    }
    this.spinnerService.loadSpinner();
    try {
      await this.appApiKeysService.update(this.editForm.id, {
        name: this.editForm.name.trim(),
        isActive: this.editForm.isActive,
        expiresAt: this.editForm.expiresAt
          ? this.editForm.expiresAt.toISOString()
          : null,
        permissions_ref: this.editForm.permissions_ref ?? [],
      });
      this.editVisible = false;
      this.messageService.add({
        severity: 'success',
        summary: 'Saved',
        detail: 'API key updated.',
      });
      await this.loadKeys();
    } catch (e) {
      this.handleHttpError(e, 'Update API key');
    } finally {
      this.spinnerService.endSpinner();
    }
  }

  confirmRotate(row: AppApiKeyTableRow): void {
    this.confirmationService.confirm({
      message:
        `Rotate the secret for "${row.name}"? The old key will stop working immediately.`,
      header: 'Rotate API key',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        this.spinnerService.loadSpinner();
        try {
          const result = await this.appApiKeysService.rotate(row.id);
          await this.loadKeys();
          this.showSecretDialog('New API key', result.apiKey);
        } catch (e) {
          this.handleHttpError(e, 'Rotate API key');
        } finally {
          this.spinnerService.endSpinner();
        }
      },
    });
  }

  confirmDelete(row: AppApiKeyTableRow): void {
    this.confirmationService.confirm({
      message: `Delete API key "${row.name}" (id ${row.id})? This cannot be undone.`,
      header: 'Delete API key',
      icon: 'pi pi-trash',
      acceptButtonStyleClass: 'p-button-danger',
      accept: async () => {
        this.spinnerService.loadSpinner();
        try {
          await this.appApiKeysService.delete(row.id);
          this.messageService.add({
            severity: 'success',
            summary: 'Deleted',
            detail: 'API key removed.',
          });
          await this.loadKeys();
        } catch (e) {
          this.handleHttpError(e, 'Delete API key');
        } finally {
          this.spinnerService.endSpinner();
        }
      },
    });
  }

  showSecretDialog(title: string, apiKey: string): void {
    this.secretTitle = title;
    this.secretValue = apiKey;
    this.secretVisible = true;
  }

  copySecret(): void {
    void navigator.clipboard.writeText(this.secretValue).then(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Copied',
        detail: 'Key copied to clipboard.',
      });
    });
  }

  private handleHttpError(error: unknown, context: string): void {
    let detail = 'Request failed.';
    if (error instanceof HttpErrorResponse) {
      detail =
        typeof error.error === 'string'
          ? error.error
          : error.error?.message || error.message || detail;
    }
    this.messageService.add({
      severity: 'error',
      summary: context,
      detail,
    });
  }
}
